import React, { useCallback } from 'react'
import { Grid2, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { required, zipCode } from 'utils/formValidation'

import { KAddressField } from 'components/core/KAddressField'
import { KSwitchField } from 'components/core/KSwitch'
import { KTextField } from 'components/core/KTextField'
import { KZipCodeField } from 'components/core/KZipCodeField'

import { Place } from 'models'
import { AddressType } from 'models/Address'

export interface AddressParams {
  place?: Place
  place_id?: string
  street?: string
  address?: string
  city?: string
  state?: string
  zip?: string
  country?: string
  phoneNumber?: string
  default?: boolean
}

export interface AddressFormProps {
  form: ReturnType<typeof useForm<AddressParams>>
  addressType: AddressType
}

export const getAddressLabels = (
  addressType: AddressType,
  t: (key: string) => string,
) => {
  const typeLabel = addressType === AddressType.Billing ? 'billing' : 'delivery'
  const capitalizedTypeLabel =
    addressType === AddressType.Billing ? 'Billing' : 'Delivery'

  return {
    addressLabel: t(`${capitalizedTypeLabel} address`),
    addressRequired: t(`${capitalizedTypeLabel} address is required`),
    cityRequired: t(`${capitalizedTypeLabel} city is required`),
    stateRequired: t(`${capitalizedTypeLabel} state is required`),
    dialogTitle: (editable: boolean) =>
      editable
        ? t(`Edit ${typeLabel} address`)
        : t(`Add new ${typeLabel} address`),
    actionLabel: (editable: boolean) =>
      editable ? t('Save Changes') : t('Add new address'),
  }
}

export const AddressEditForm = ({ form, addressType }: AddressFormProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const {
    control,
    register,
    setValue,
    clearErrors,
    formState: { errors, touchedFields },
    trigger,
  } = form

  const { addressLabel, addressRequired, cityRequired, stateRequired } =
    getAddressLabels(addressType, t)

  const setAddressFields = useCallback(
    (place?: Place) => {
      const options = {
        shouldTouch: false,
        shouldValidate: true,
        shouldDirty: true,
      }

      // Handle Google place selection
      if (place) {
        setValue('place', place, options)
        setValue('place_id', place.place_id, options)
        setValue('street', place.street, options)
        setValue('address', place.description, options)
        setValue('city', place.city, options)
        setValue('state', place.state, options)
        setValue('zip', place.zip_code, options)

        clearErrors(['place', 'address', 'city', 'state', 'zip'])
      } else {
        // Only clear the place-related fields when removing selection
        setValue('place', undefined, options)
        setValue('place_id', undefined, options)
        setValue('street', undefined, options)
        clearErrors('place')
      }

      // Directly trigger validation for the fields
      trigger(['address', 'city', 'state', 'zip']).catch(() => {
        // Handle any potential errors from trigger silently
        // This ensures the UI doesn't break if validation fails
      })
    },
    [setValue, clearErrors, trigger],
  )

  // Handle manual input for address fields
  const handleManualInput = () => {
    setValue('place', undefined)
    setValue('place_id', undefined)
    setValue('street', undefined)
  }

  return (
    <Stack sx={{ backgroundColor: theme.palette.background.default }}>
      <Grid2 container spacing={2.5}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Controller
            name="place"
            control={control}
            render={({ field }) => (
              <KAddressField
                label={t('Search your address')}
                helperText={errors?.place?.message}
                error={!!errors?.place && !!touchedFields.place}
                value={field.value}
                onChange={setAddressFields}
                ref={field.ref}
              />
            )}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <KTextField
            required
            label={addressLabel}
            helperText={
              touchedFields.address ? errors?.address?.message : undefined
            }
            error={!!errors?.address && !!touchedFields.address}
            {...register('address', required(t, 'Address', addressRequired))}
            onChange={(e) => {
              register('address').onChange(e)
              handleManualInput()
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <KTextField
            required
            label={t('City')}
            helperText={touchedFields.city ? errors?.city?.message : undefined}
            error={!!errors?.city && !!touchedFields.city}
            {...register('city', required(t, 'City', cityRequired))}
            onChange={(e) => {
              register('city').onChange(e)
              handleManualInput()
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <KTextField
            required
            label={t('State')}
            helperText={
              touchedFields.state ? errors?.state?.message : undefined
            }
            error={!!errors?.state && !!touchedFields.state}
            {...register('state', required(t, 'State', stateRequired))}
            onChange={(e) => {
              register('state').onChange(e)
              handleManualInput()
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <KZipCodeField
            required
            label={t('Zip code')}
            helperText={touchedFields.zip ? errors?.zip?.message : undefined}
            error={!!errors?.zip && !!touchedFields.zip}
            {...register('zip', zipCode(t))}
            onChange={(e) => {
              register('zip').onChange(e)
              handleManualInput()
            }}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <Stack
            direction="row"
            sx={{
              height: '100%',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
            }}
          >
            <Controller
              name="default"
              control={control}
              render={({ field }) => (
                <KSwitchField
                  label={t('Set as default address')}
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
        </Grid2>
      </Grid2>
    </Stack>
  )
}
